import { useEffect, useRef, useState } from 'react'

import { Button, Divider, Flex, HStack, PinInput, PinInputField, Text } from '@chakra-ui/react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'

import {
    usePostRegisterDataMutation,
    useSendMobileVerificationCodeMutation,
    useSendSmsMutation,
} from '@core/app/api/authApi'
import {
    addStepCount,
    address,
    allStepsCount,
    dateOfBirth,
    departments,
    email,
    firstName,
    haveDepartments,
    lastName,
    mobilePhone,
    nextStep,
    removeStepCount,
    repeatEmail,
    serviceId,
    setMobileVerificationId,
    stepCount,
    userAgreement,
    verificationEmailId,
    verificationMobileId,
    cv,
} from '@core/app/slices/auth'
import type { AuthApiFields } from '@core/app/types/authApiType'
import { useAppDispatch, useAppSelector } from '@core/utils/hooks/hook'

type PhoneVerificationStepProps = {
    gender: '0' | '1' | '2' | '3' | '4'
    fields?: AuthApiFields | undefined
    linkedinUrl?: string | null
}

export const PhoneVerificationStep = ({ gender, fields, linkedinUrl }: PhoneVerificationStepProps) => {
    const dispatch = useAppDispatch()
    const stepsCount = useAppSelector(allStepsCount)
    const stepNumber = useAppSelector(stepCount)
    const isDepartmentsExists = useAppSelector(haveDepartments)
    const emailValue = useAppSelector(email)
    const repeatEmailValue = useAppSelector(repeatEmail)
    const firstNameValue = useAppSelector(firstName)
    const lastNameValue = useAppSelector(lastName)
    const userAgreementValue = useAppSelector(userAgreement)
    const mobilePhoneValue = useAppSelector(mobilePhone)
    const addressValue = useAppSelector(address)
    const dateOfBirthValue = useAppSelector(dateOfBirth)
    const departmentsValue = useAppSelector(departments)
    const emailId = useAppSelector(verificationEmailId)
    const mobileId = useAppSelector(verificationMobileId)
    const serviceIdValue = useAppSelector(serviceId)
    const cvState = useAppSelector(cv)
    const [err, setErr] = useState<string>('')

    const [sendCode] = useSendSmsMutation()
    const [sendMobileVerificationCode] = useSendMobileVerificationCodeMutation()
    const [sendData, errors] = usePostRegisterDataMutation()

    const [count, setCount] = useState<number>(90)
    const { executeRecaptcha } = useGoogleReCaptcha()

    const { t } = useTranslation()

    const submitHandler = async (mobileId: number) => {
        if (!executeRecaptcha) {
            console.warn('ReCAPTCHA not available');
            return;
        }

        const token = await executeRecaptcha('api_register')
        if (token) {
            try {
                const addressData =
                    fields?.address.visible &&
                    ((addressValue.country || '').trim().length > 0 || (addressValue.city || '').trim().length > 0)
                        ? {
                              address1: addressValue.address1,
                              country: addressValue.country,
                              city: addressValue.city,
                              postalCode: addressValue.postalCode,
                          }
                        : undefined

                const response = await sendData({
                    data: {
                        validateMobileId: mobileId !== 0 ? mobileId : undefined,
                        validateEmailId: emailId !== 0 ? emailId : undefined,
                        serviceUid: serviceIdValue ? serviceIdValue : undefined,
                        email: emailValue,
                        repeatEmail: repeatEmailValue,
                        firstName: firstNameValue,
                        lastName: lastNameValue,
                        userAgreement: userAgreementValue,
                        password: "",
                        // mobilePhone: {
                        //     prefix: mobilePhoneValue.prefix,
                        //     number: mobilePhoneValue.number.toString(),
                        // },
                        mobilePhone:
                            fields?.mobilePhone.visible &&
                            ((mobilePhoneValue.prefix || '').trim().length > 1 ||
                                String(mobilePhoneValue.number || '').trim().length > 0)
                                ? {
                                      prefix: mobilePhoneValue.prefix,
                                      number: mobilePhoneValue.number,
                                  }
                                : undefined,
                        // address: {
                        //     address1: addressValue.address1,
                        //     country: addressValue.country,
                        //     city: addressValue.city,
                        //     postalCode: addressValue.postalCode,
                        // },
                        address: addressData,
                        dateOfBirth: fields?.dateOfBirth.visible ? dateOfBirthValue : undefined,
                        departments: departmentsValue ? departmentsValue : undefined,
                        'g-recaptcha-response': token,
                        gender: fields?.gender.visible && +gender ? +gender : undefined,
                        linkedinUrl,
                        cv: cvState,
                    },
                }).unwrap()

                location.href = response.redirectUrl
                localStorage.setItem('is_logged', 'true')
                localStorage.removeItem('apply_dialog')
            } catch (error) {
                // TODO
                console.log(errors)
            }
        }
    }

    const sendCodeHandler = async (code: string) => {
        try {
            const response = await sendMobileVerificationCode({
                data: {
                    code,
                },
            }).unwrap()

            dispatch(setMobileVerificationId(response.data.id))
            if (isDepartmentsExists) {
                dispatch(addStepCount())
                dispatch(nextStep('departments'))
            } else {
                submitHandler(response.data.id)
            }
        } catch (error: any) {
            const errorMessage = error.data?.errors?.code?.[0] || 'An error occurred. Please try again.'
            setErr(errorMessage)

            setTimeout(() => {
                dispatch(removeStepCount())
                dispatch(nextStep('personalDataExtanded'))
            }, 1500)
        }
    }

    const resendCode = async () => {
        try {
            if (!executeRecaptcha) {
                console.warn('ReCAPTCHA not available');
                return;
            }

            const token = await executeRecaptcha('api_phone_send_sms')

            if (token) {
                await sendCode({
                    data: {
                        'g-recaptcha-response': token,
                        mobilePhone: mobilePhoneValue,
                    },
                }).unwrap()
            }

            setCount(90)
        } catch (error) {
            // TODO
            console.log(error)
        }
    }

    useEffect(() => {
        if (count === 0) return

        const interval = setInterval(() => {
            setCount((prev) => prev - 1)
        }, 1000)
        return () => clearInterval(interval)
    }, [count])

    return (
        <Flex flexDirection={'column'} className={'phoneVerification'}>
            <Text fontSize={'xs'} color={'text.thirdly'} mb={1}>
                {t('6593')} {stepNumber} {t('1165')} {stepsCount}
            </Text>
            <Text fontSize={'2xl'} color={'text.main'} fontWeight={'semibold'} mb={4}>
                {t('6481')}
            </Text>
            <Text color={'text.secondary'}>{t('6588')}</Text>
            <Text variant={'noMargins'} color={'text.link'} fontWeight={'bold'}>
                {`${mobilePhoneValue.prefix} ${mobilePhoneValue.number}`}
            </Text>
            <HStack mt={10}>
                <PinInput
                    otp
                    placeholder={''}
                    size={'lg'}
                    autoFocus={true}
                    focusBorderColor={'state.primary'}
                    type={'number'}
                    onComplete={(value) => sendCodeHandler(value)}
                    onChange={() => setErr('')}
                    isInvalid={err.length > 0}
                    errorBorderColor={'formElements.borderError'}
                >
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField w={'51px'} h={'60px'} />
                </PinInput>
            </HStack>
            {err && (
                <Text fontSize={'xs'} color={'text.error'} mt={1}>
                    {err}
                </Text>
            )}
            <Text fontSize={'sm'} color={'text.thirdly'} mt={2}>
                {t('6589')}{' '}
                <Text as="b" color={'text.secondary'}>
                    {count}
                </Text>{' '}
                {t('6590')}
            </Text>
            <Divider mt={'112px'} />
            <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'} mt={6}>
                {t('6591')}.
            </Text>
            {count === 0 ? (
                <Button variant={'link'} color={'text.link'} mt={3} alignSelf={'start'} onClick={() => resendCode()}>
                    {t('6592')}
                </Button>
            ) : null}
        </Flex>
    )
}
